import axios from "axios";

function resolveApiBase() {
  // Override explícito (build/deploy).
  const envUrl = (import.meta as any).env?.VITE_API_URL;
  if (envUrl) return envUrl as string;
  // Default: caminho relativo. O Vite proxia /api -> backend, e em produção
  // (mesma origem) também funciona sem mexer em CORS/tunnel.
  return "/api";
}

export const API_BASE = resolveApiBase();
const REFRESH_URL = `${API_BASE}/auth/refresh`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - auto-refresh on 401
let isRefreshing = false;
let failedQueue: Array<{ resolve: (v: unknown) => void; reject: (e: unknown) => void }> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Network error / backend down → dispara evento pra o banner reagir IMEDIATO
    const isNetworkError = !error.response && (
      error.code === "ERR_NETWORK" ||
      error.code === "ECONNABORTED" ||
      error.code === "ECONNREFUSED" ||
      error.message === "Network Error" ||
      error.message?.includes("timeout")
    );
    if (isNetworkError) {
      // Não dispara em /ping (já é o monitor) pra evitar loop
      const url: string = originalRequest?.url || "";
      if (!url.endsWith("/ping")) {
        try { window.dispatchEvent(new CustomEvent("growsoft:rede-falhou", { detail: { url, code: error.code } })); } catch {}
      }

      // Retry transparente: backend cai por ~1s quando salvo arquivo durante dev.
      // Repete 1x após 500ms se for método idempotente (GET/HEAD) e ainda não tentou.
      const metodo = (originalRequest?.method || "get").toLowerCase();
      const idempotente = metodo === "get" || metodo === "head";
      if (idempotente && originalRequest && !originalRequest._netRetry) {
        originalRequest._netRetry = true;
        await new Promise((r) => setTimeout(r, 500));
        return api(originalRequest);
      }
    }

    // 401 vindo da própria tela de login/refresh: deixa a UI tratar o erro,
    // não dispara reload nem fluxo de refresh (não há sessão pra renovar).
    const reqUrl: string = originalRequest?.url || "";
    const isAuthEndpoint = reqUrl.includes("/auth/login") || reqUrl.includes("/auth/refresh");
    if (error.response?.status === 401 && isAuthEndpoint) {
      return Promise.reject(error);
    }

    // If not a 401, or already retried, or no refresh token available → logout
    if (error.response?.status !== 401 || originalRequest._retry) {
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("growsoft_auth");
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }

    // Try refresh
    const stored = localStorage.getItem("growsoft_auth");
    const authData = stored ? JSON.parse(stored) : null;
    const refreshToken = authData?.refreshToken;

    if (!refreshToken) {
      localStorage.removeItem("token");
      localStorage.removeItem("growsoft_auth");
      window.location.href = "/login";
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue this request until refresh completes
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const response = await axios.post(REFRESH_URL, { refreshToken });

      const newToken = response.data.data.token;
      localStorage.setItem("token", newToken);

      // Update auth store in localStorage
      if (authData) {
        authData.token = newToken;
        localStorage.setItem("growsoft_auth", JSON.stringify(authData));
      }

      processQueue(null, newToken);

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      localStorage.removeItem("token");
      localStorage.removeItem("growsoft_auth");
      window.location.href = "/login";
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
